<script lang="ts">
  import { useRegisterSW } from 'virtual:pwa-register/svelte';
  // `onMount` і `createEventDispatcher` імпортувалися сюди й не вживалися
  // жодного разу — жодної події цей компонент не надсилає.
  import { onDestroy } from "svelte";
  import { logService } from "$lib/services/logService.svelte";
  import { t } from "$lib/i18n/typedI18n";
  import StyledButton from "$lib/components/ui/StyledButton.svelte";

  // Інтервал перевірки оновлень (наприклад, кожні 15 хвилин)
  const CHECK_INTERVAL = 15 * 60 * 1000;

  /*
   * Ідентифікатор інтервалу тримається, щоб зняти його при знищенні
   * (SVELTE-CORE-v9 § 2.2.2, `SC-LISTENER-CLEANUP`). Доти `setInterval` жив
   * без пари: на SPA-навігації переживав компонент, а `r.update()` продовжував
   * ходити по мережі від кожного зайвого монтування.
   */
  let updateCheckId: ReturnType<typeof setInterval> | null = null;

  const { needRefresh, updateServiceWorker, offlineReady } = useRegisterSW({
    onRegistered(r) {
      logService.info('[PWA] Service Worker registered');
      if (r) {
        updateCheckId = setInterval(() => {
          logService.info('[PWA] Checking for updates and validating version...');
          r.update();
        }, CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      logService.error('[PWA] Service Worker registration failed', error);
    },
  });

  onDestroy(() => {
    if (updateCheckId !== null) clearInterval(updateCheckId);
    updateCheckId = null;
  });

  const close = () => {
    offlineReady.set(false);
    needRefresh.set(false);
  };

  const update = async () => {
    logService.action('[PWA] User confirmed update. Updating SW...');
    await updateServiceWorker(true); // true = force reload
  };
</script>

{#if $needRefresh}
  <div class="pwa-toast" role="alert" aria-labelledby="toast-message">
    <div class="text-container">
      <p class="title">{$t("updateNotification.title")}</p>
      <p class="description">{$t("updateNotification.description")}</p>
    </div>
    <div class="pwa-buttons">
      <StyledButton variant="primary" onclick={update} style="width: 100%; margin-bottom: 8px;">
        {$t("updateNotification.updateButton")}
      </StyledButton>
      <button onclick={close} class="dismiss-btn">
        {$t("updateNotification.dismissButton")}
      </button>
    </div>
  </div>
{/if}

<style>
  .pwa-toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--bg-secondary);
    color: var(--text-primary);
    padding: 20px 24px;
    border-radius: 16px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
    z-index: 10002; /* Трохи вище інших */
    border: var(--global-border-width) solid var(--control-selected);
    animation: slide-in 0.5s ease-out;
    width: 90vw;
    max-width: 380px;
    box-sizing: border-box;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  @keyframes slide-in {
    from {
      transform: translate(-50%, 120px);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }

  .text-container {
    margin-bottom: 0;
  }

  .title {
    margin: 0 0 8px 0;
    font-weight: 700;
    font-size: 1.2em;
  }

  .description {
    margin: 0;
    font-size: 0.95em;
    opacity: 0.9;
    line-height: 1.5;
  }

  .pwa-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .dismiss-btn {
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 8px;
    font-size: 0.9em;
    text-decoration: underline;
  }
  .dismiss-btn:hover {
    color: var(--text-primary);
  }
</style>
